import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { projectConnectionGroupMutations } from "@/queries/project-connection-groups/mutations";
import { useProjectConnectionGroupQueries } from "@/queries/project-connection-groups/queryOptions";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";

import { ProjectConnectionGroupsList, type ProjectConnectionGroupsListProps } from "./ProjectConnectionGroupsList";

const PROJECT_CONNECTION_GROUPS_LIMIT = 20;

export function ListProjectConnectionGroupsPage(): React.ReactNode {
  const [page, setPage] = useState<number>(0);
  const { id: connectionId } = useParams<{ id: string }>();

  const projectConnectionGroupsQueryParams = {
    Offset: page * PROJECT_CONNECTION_GROUPS_LIMIT,
    Limit: PROJECT_CONNECTION_GROUPS_LIMIT,
  };

  const deleteGroupMutation = projectConnectionGroupMutations.useDeleteProjectConnectionGroup(connectionId!);

  const projectConnectionQueries = useProjectConnectionQueries();
  const projectConnectionGroupQueries = useProjectConnectionGroupQueries();

  const {
    data: projectConnection,
    isPending: isPendingProjectConnection,
    error: errorProjectConnection,
  } = useQuery(projectConnectionQueries.details(connectionId!));

  const {
    data: projectConnectionGroups,
    isFetching: isFetchingProjectConnectionGroups,
    error: errorLoadingProjectConnectionGroups,
  } = useQuery({
    ...projectConnectionGroupQueries.list(connectionId!, projectConnectionGroupsQueryParams),
    placeholderData: keepPreviousData,
  });

  const error = !connectionId || errorLoadingProjectConnectionGroups || errorProjectConnection;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isPendingProjectConnection;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const totalPages = projectConnectionGroups
    ? Math.ceil(projectConnectionGroups.total / PROJECT_CONNECTION_GROUPS_LIMIT)
    : 0;

  const contentProps: ProjectConnectionGroupsListProps = {
    projectConnection,
    projectConnectionGroups: projectConnectionGroups?.items ?? [],
    isLoading: isFetchingProjectConnectionGroups,
    page,
    onPageChange: setPage,
    totalPages,
    onDelete: deleteGroupMutation.mutateAsync,
  };

  return <ProjectConnectionGroupsList {...contentProps} />;
}
